import { useState, useRef, useEffect } from 'react';
import { useTranslation, type Locale } from '@/i18n/i18n';

const localeLabels: Record<Locale, { flag: string; label: string }> = {
  pl: { flag: '\u{1F1F5}\u{1F1F1}', label: 'PL' },
  en: { flag: '\u{1F1EC}\u{1F1E7}', label: 'EN' },
  uk: { flag: '\u{1F1FA}\u{1F1E6}', label: 'UK' },
};

export function LanguageSwitcher() {
  const { locale, setLocale, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const current = localeLabels[locale];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
        aria-label={t('langSwitcher.label')}
        aria-expanded={open}
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span>{current.label}</span>
        <svg
          className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[100px] z-50">
          {(Object.entries(localeLabels) as [Locale, { flag: string; label: string }][]).map(
            ([key, { flag, label }]) => (
              <button
                key={key}
                onClick={() => {
                  setLocale(key);
                  setOpen(false);
                }}
                className={`flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted transition-colors ${
                  key === locale ? 'font-bold text-primary' : 'text-foreground'
                }`}
              >
                <span className="text-base leading-none">{flag}</span>
                <span>{label}</span>
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}
